import $ from "/js/libs/jquery-3.3.1.min.js"
import "../../libs/limacharlie.helpers.js";
import {gsap} from "/js/libs/gsap/all.js"
import {i18n} from '/js/app/base/i18n.js'
import {AudioPlayer} from './Audio.js'
import nunjucks from "/js/libs/nunjucks.js"

const remote = inElectron() ? global.require('electron').remote : false;

export class BaseGame {

  constructor(dataFile = 'data/data.json') {
    this.ready = false;
    this.started = false;
    this.lockScreen = false;

    // Data and settings file
    this.dataFile = dataFile;
    this.data = {};
    this.settingsFile = 'settings.json';

    // Hold current screen
    this.currentScreen = 0;
    this.targetScreen = null;

    // Hold current action (in progress)
    this.currentAction = false;

    // Touch event valid with the NWJS  version <= 38
    this.touchEvent = (('ontouchstart' in window) || window.navigator.maxTouchPoints > 0 || (window.DocumentTouch && document instanceof DocumentTouch)) && (window.getURLParameter("forcetouchevent") != "click") ? "touchstart" : "click";

    // Add accessibility features
    this.touchEvent += " keydown";

    // Init i18n
    this.i18n = new i18n(this);

    // Init Audio
    this.audio = new AudioPlayer(this);

    // Init buttons
    this.initButtons();

    // When opened in Electron, disable right-click
    if (inElectron()) {
      $("#main").addClass("in-electron")
      $("body").css("cursor", "none").on("contextmenu", function (e) {
        return false;
      });
      $(document).contextmenu(function (e) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      });
    } else {
      $("#main").addClass("in-browser")
      $(window).keypress(function (e) {
        if (e.key === ' ' || e.key === 'Spacebar') {
          $("#ref").toggle();
        }
      })
    }

    // Screensaver
    this.screensaverDelay = 30;
    this.lockScreenSaver = false;
    this.lastClick = 0;
    this.lastReset = 0;
  }

  // Load settings JSON (with promise)
  loadSettings() {
    return new Promise((resolve, reject) => {
      $.ajax({
        dataType: "json",
        url: this.settingsFile,
        cache: false,
      }).done((data) => {
        this.settings = data;
        // Init screensaver
        if (typeof this.settings.screensaver_delay != "undefined") {
          this.screensaverDelay = this.settings.screensaver_delay;
        }
        resolve();
      }).fail(() => {
        alert("The settings.json file was not found. Please add the setting file and restart app.");
        if (remote) {
          remote.app.quit();
        }
        reject(new Error("The settings.json file was not found. Please add the setting file and restart app."));
      });
    });
  };

  // Load data JSON (with promise)
  loadData(filename = null) {
    return new Promise((resolve, reject) => {
      $.ajax({
        dataType: "json",
        url: filename ?? this.dataFile,
        cache: false,
        success: (data) => {
          this.data = {...this.data, ...data};
          resolve();
        },
        error: () => {
          alert("No data found. Please add the data file and restart the app.");
          reject();
        }
      });
    });
  };

  // Preload audio files
  preloadAudio() {
    const allAudioFiles = this.findAllAudioFiles(this.data);
    if (typeof this.settings.click_sound !== "undefined" && this.settings.click_sound != "") {
      allAudioFiles.push(this.settings.click_sound);
    }
    const uniqueAudioFiles = allAudioFiles.filter((value, index, array) => array.indexOf(value) === index);

    // Preload audio
    return this.audio.preload(uniqueAudioFiles)
  };

  // Init nunjucks
  initNunjucks() {
    return new Promise((resolve, reject) => {
      this.nunjuckEnv = nunjucks.configure('', {
        autoescape: false,
      });
      this.mainNunjuckTemplate = nunjucks.compile($("#main").html(), this.nunjuckEnv);
      resolve();
    });
  }

  // Render twig style with nunjucks
  renderNunjucks() {
    return new Promise((resolve, reject) => {
      const res = this.mainNunjuckTemplate.render({data: this.data, visitorData: null, app: this});
      $("#main").show().html(res)
      $("img").each(function (i, img) {
        if ($(img).data("src")) {
          $(img).attr("src", $(img).data("src"))
        }
      });
      resolve();
    });
  }

  // Update specific nunjuck templates marked as updatable twig style with nunjucks
  updateNunjucks() {
    return new Promise((resolve, reject) => {
      const res = this.mainNunjuckTemplate.render({data: this.data, visitorData: this.currentVisitorData, app: this});
      const $res = $(`<div>${res}</div>`);
      $res.find("img").each(function (i, img) {
        if ($(img).data("src")) {
          $(img).attr("src", $(img).data("src"))
        }
      });
      $res.find("*[data-updatable]").each((i, n) => {
        const id = $(n).attr("data-updatable")
        $("#main").find(`[data-updatable='${id}']`).replaceWith(n)
      })
      resolve();
    });
  }

  initScreenSaver() {
    // Timeout (screensaver) related
    this.resetTimer();
    $(document).on("click touchmove touchstart touchend", (e) => {
      this.resetTimer();
    });
    requestInterval(() => this.checkTimer(), 1000);
  }

  initButtons() {

    // Init big buttons dynamic action
    $("#main").on(this.touchEvent, ".help, .trigger, .button, .click-sound", (e) => {

      if (e.type === 'keydown' && !(e.keyCode === 13 || e.keyCode === 32)) {
        return;
      }

      if (this.currentAction !== false) {
        return;
      }

      const el = $(e.currentTarget);
      if (typeof el.data("action") != "undefined") {
        const action = el.data("action")

        // Change context when needed
        // Allow us to call subclass functions inside baseGame
        const actionParts = action.split('.');
        let context = this;
        let methodName = actionParts[0];

        if (actionParts.length > 1) {
          context = this[actionParts[0]];
          methodName = actionParts[1];
        }

        if (typeof context[methodName] == "function") {
          // Action is a function
          const functionArg = (typeof el.data("action-arg") != "undefined" ? el.data("action-arg") : null);
          const functionDelay = (typeof el.data("action-delay") != "undefined" ? el.data("action-delay") : 50);
          console.log("BaseGame: Calling function : " + action + "(" + ((functionArg !== null) ? functionArg : "") + ") in " + functionDelay + " millis");

          requestTimeout((e) => {
            if (functionArg !== null) {
              context[methodName](functionArg, e);
            } else {
              context[methodName](e);
            }
            this.currentAction = false;
          }, functionDelay);
        } else {
          console.log(`BaseGame: Action not found for ${action}`);
        }
      }

      // Play audio click
      if (typeof this.settings.click_sound !== "undefined" && this.settings.click_sound != "") {
        this.audio.play(this.settings.click_sound, this.settings.volume.click);
        // console.log("click")
      }

    });

  };

  start() {
    if (!this.ready || this.started) {
      return;
    }

    this.started = true;

    // To be completed in child games
  }

  goToScreen(screenId, callback, skipLockScreenCheck = false) {

    // Stop on lock
    if (!skipLockScreenCheck && this.lockScreen) {
      console.log("prevent locked screen animation")
      return false;
    }

    // Prevent requesting a re-animation of the same screen
    // while being animated to, or currently on a screen.
    if (this.targetScreen === screenId) {
      console.log("prevent same screen animation")
      return;
    }

    this.targetScreen = screenId;
    this.lockScreen = true;
    $("body").css("pointer-events", "none");

    if (this.screenTimeline) {
      this.screenTimeline.kill();
      this.screenTimeline = null;
    }

    const currentScreen = $(".screen.current");
    const nextScreen = $(`#screen-${screenId}`);
    this.screenTimeline = gsap.timeline();

    if (currentScreen.length > 1) {
      console.warn("There are multiple screens marked as `current`, which is not a good thing and may create multiple callbacks.")
    }

    //Animate in/out + callback
    if (currentScreen.length > 0 && nextScreen.length > 0) {

      // If there is a current screen and a new screen, fade out current and fade in new, then callback
      currentScreen.removeClass("current");

      this.screenTimeline.to(currentScreen, {
        alpha: 0,
        duration: 0.4,
      })

      this.screenTimeline.add(() => {
        $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
        $(".screen").addClass("hidden");
        this.initScreen(screenId);
        nextScreen.removeClass("hidden");
      });

      this.screenTimeline.to(nextScreen, {
        alpha: 1,
        duration: 0.4,
      })

      this.screenTimeline.add(() => {
        this.currentScreen = screenId;
        this.lockScreen = false;
        // this.targetScreen = null;
        $("body").css("pointer-events", "");
        $("#screen-" + screenId).addClass("current");
        if (typeof callback == "function") {
          callback();
        }
      });

    } else if (currentScreen.length === 0 && nextScreen.length > 0) {
      $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
      $(".screen").addClass("hidden");
      this.initScreen(screenId);

      // If there is no current screen and a new screen,fade in new, then callback
      nextScreen.removeClass("hidden")

      this.screenTimeline.to(nextScreen, {
        alpha: 1,
        duration: 0.4,
      });

      this.screenTimeline.add(() => {
        this.currentScreen = screenId;
        this.lockScreen = false;
        // this.targetScreen = null;
        $("body").css("pointer-events", "");
        $("#screen-" + screenId).addClass("current");
        if (typeof callback == "function") {
          callback();
        }
      });

    } else if (screenId === 0 || currentScreen.length > 0 && nextScreen.length == 0) {

      // If there is a current screen and no new screen, fade out current then callback
      currentScreen.removeClass("current")

      this.screenTimeline.to(currentScreen, {
        alpha: 0,
        duration: 0.4
      });

      this.screenTimeline.add(() => {
        $("body").removeClass("current-screen-" + this.currentScreen).addClass("current-screen-" + screenId);
        $(".screen").addClass("hidden");

        this.initScreen(screenId);

        this.currentScreen = screenId;
        this.lockScreen = false;
        // this.targetScreen = null;
        $("body").css("pointer-events", "");
        if (typeof callback == "function") {
          callback();
        }
      })
    }

    return true;
  }

  goToNextScreen(callback) {
    // Super constructor
    this.goToScreen(parseInt(this.currentScreen) + 1, callback);
  };

  goToPreviousScreen(callback) {
    // Super constructor
    this.goToScreen(parseInt(this.currentScreen) - 1, callback);
  };

  initScreen(screenId) {
    // To be overriden in child games
  }

  resetTimer() {
    this.lastClick = millis();
  }

  checkTimer(e) {
    const noActionSince = millis() - this.lastClick;
    if (noActionSince > (this.screensaverDelay * 1000) && this.screensaverDelay != -1) {
      if (this.shouldReset()) {
        this.goToScreenSaver();
      }
    }
  }

  /**
   * Prevent the game from going into screen saver mode
   * This might make sense while watching a video, reading long text, etc
   */
  pauseScreenSaver() {
    this.lockScreenSaver = true;
  }

  /**
   * Allow the game to go into screen saver mode
   */
  unpauseScreenSaver() {
    this.resetTimer();
    this.lockScreenSaver = false;
  }

  goToScreenSaver() {
    // To be overriden in child games
    this.reset(1);
  }

  shouldReset() {
    if (millis() - this.lastReset < 3000) {
      return false;
    }

    this.lastClick = millis();
    this.lastReset = millis();

    // Prevent reset if already reset
    if (this.currentScreen === 1) {
      return false;
    }

    // Prevent reset when screen saver is locked
    if (this.lockScreenSaver === true) {
      return false;
    }

    return true;
  }

  reset(screenId, resetCallback) {
    this.started = false;

    if (typeof screenId == "undefined") {
      screenId = 1;
    }

    // Return to idle screen
    this.goToScreen(screenId, () => {
      resetCallback();
    }, true);
  }

  // Preload all audio files from json files
  findAllAudioFiles(objectToParse) {
    return this.findAllExtensionFilesInObject(objectToParse, [".mp3", ".wav", ".oga"]);
  }

  findAllExtensionFilesInObject(objectToParse, extensions) {
    let result = [];

    $.each(objectToParse, (i, element) => {
      if (element instanceof Array || element instanceof Object) {
        const ids = this.findAllExtensionFilesInObject(element, extensions);
        result = result.concat(ids); // or $.merge(result, ids);
      } else if (typeof element == "string") {
        $.each(extensions, (i2, extension) => {
          if (element.endsWith(extension)) {
            result.push(element);
          }
        });
      }

    });

    return result;
  }

  restartApp = function () {
    if (inElectron()) {
      remote.getCurrentWindow().webContents.reloadIgnoringCache();
      // remote.app.relaunch()
      // remote.app.exit(0)
    }
  }
}
